import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {FieldComponent} from '../FieldComponent';
import {RadioButton} from './RadioButton';
import styles from './RadioButton-hashed.scss';
import {withTheme} from '@elixir/theme';

// default available values if prop not passed to component
const defaultAvailableValues = [
    {
        code: 'true',
        name: {
            id: 'platform.inputs.Yes',
            defaultMessage: 'Yes'
        }
    },
    {
        code: 'false',
        name: {
            id: 'platform.inputs.No',
            defaultMessage: 'No'
        }
    }
];

/**
 * RadioButtonField is a 'field' component that displays a label, control and message. It can be used to render
 * two <button> elements that allow toggle between two values.
 */
export class RadioButtonField extends FieldComponent {

    static propTypes = {
        ...FieldComponent.propTypes,
        /**
         * Boolean or string value
         */
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        /**
         * Default value
         */
        defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        /**
         * Array of choice objects to display; choice objects contains 'code' and 'name'; if not provided, default 'Yes'/'No' will be used
         */
        availableValues: PropTypes.array
    };

    // Default props
    static defaultProps = Object.assign(
        {},
        FieldComponent.defaultProps,
        {
            availableValues: defaultAvailableValues
        }
    );

    // explicitly defined due to docgen limitations
    render() {
        return super.render();
    }

    /**
     * Handles changing data model value underneath
     *
     * @param {any} targetValue - value coming from onChange event target
     */
    handleValueChange = (targetValue) => {
        // convert 'true', 'false' string to boolean; leave the rest as specified
        let value;
        if (targetValue === 'true') {
            value = true;
        } else if (targetValue === 'false') {
            value = false;
        } else {
            value = targetValue;
        }
        this.notifyChange(value);
    }

    /**
     * Render the radio buttons for the specified options
     *
     * @param {Array} options - options to render
     * @returns {ReactElement} JSX for the radio buttons
     */
    renderRadioOptions(options) {
        const {
            id, value, disabled, required, theme
        } = this.props;
        return options.map(option => {
            return (<RadioButton
                key={option.code}
                option={option}
                id={id}
                value={value}
                onValueChange={this.handleValueChange}
                disabled={disabled}
                required={required}
                theme={theme}
                {...this.generateDataPathProperty()}
            />);
        });
    }

    /**
     * Render control for this component.
     *
     * @returns {ReactElement} JSX for the control
     */
    renderControl() {
        const {
            id, availableValues, label, theme, controlClassName
        } = this.props;

        const { translator } = this;
        const themeStyles = theme.getStyles('RadioButton', styles);
        const classes = cx(themeStyles.radioButton, controlClassName);

        const options = this.renderRadioOptions(availableValues);

        const control = (
            <div id={id} className={classes} role="radiogroup" aria-label={translator(label)}>
                {options}
            </div>
        );

        return control;
    }
}

export default withTheme(RadioButtonField);
